/**
 * Signals — the input box that is never quoted.
 *
 * THE BOX COMES FIRST. Three explainer tiles used to stand between the heading and the textarea,
 * which put the one thing this panel exists for below the fold on a phone: somebody opening
 * Signals to say a hard thing was met with a lesson about taxonomy. The order is now description,
 * box, then the kind — because the kind is a steer for the drafter and not a filing decision, and
 * it is easier to choose once the sentence exists.
 *
 * The author reviews the whole plan in ONE pass: read how each step will look on the other
 * phone, see how easily it could be traced back, edit any phrasing, ask for a rewrite in a named
 * tone, strike anything, approve the rest. After that, timing belongs to the app; even the author
 * never sees when steps will fire, which is why nothing here renders a schedule.
 *
 * A rewrite is the one thing on this screen that changes words the author did not type, so it is
 * the one thing that has to show its working: the phrasing it replaced stays pinned above it and
 * the words it moved are marked, beside a trace line that can then be seen to move with them.
 *
 * Master–detail: the list of signals and the open one. Wide gets both; narrow pushes the detail.
 */
import { Chip } from '@/components/chip';
import { Empty } from '@/components/empty';
import { Panel } from '@/components/panel';
import { StampBand, STAMP_HOLD_MS } from '@/components/stamp-band';
import { MarkedPhrasing, StepPreview } from '@/components/step-preview';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { cn } from '@/lib/utils';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { usePoll } from '@/usePoll';
import { useStore } from '@/store';
import * as React from 'react';
import { Pressable, View } from 'react-native';

type Kind = 'request' | 'desire' | 'frustration';

interface SignalSummary {
  id: string;
  kind: Kind;
  body: string;
  status: string;
  createdAt: string;
}

interface PlanStep {
  id: string;
  seq: number;
  panel: string;
  phrasing: string;
  payload: Record<string, unknown> | null;
  status: string;
  traceability: 'low' | 'medium' | 'high';
  traceNote: string;
  /**
   * What this step is CALLED, from the server.
   *
   * It used to be PANEL_LABELS here — the fifth copy of "the six panels", and the one nobody
   * would think to edit when a seventh arrived, because it lives in a different language in a
   * different folder. It is `ai_capabilities.label` now.
   */
  label: string;
  surfaced: boolean;
  done: boolean;
  withdrawn: boolean;
  expired: boolean;
  redrafting: boolean;
}

/**
 * One line of what actually happened, in the game's own words.
 *
 * Every `line` is a value of `ai_outcomes.author_copy`, chosen server-side from an allow-list, and
 * `week` is the only granularity there is — "this week", "last week", "earlier". There is no
 * timestamp to render because the server does not send one: a clock time is a moment the author
 * could watch their partner for a reaction around, which every delay in the surfacing engine
 * exists to prevent. Silent outcomes — a skip, a deleted item, a declined week — are simply
 * absent, and are meant to be indistinguishable from a step that is still out.
 */
interface TimelineLine {
  week: string;
  line: string;
}

interface SignalDetail extends SignalSummary {
  plan: { id: string; summary: string | null; status: string; steps: PlanStep[] } | null;
  timeline: TimelineLine[];
  /**
   * Why the last draft failed, when there is something the author can do about it — currently
   * only the drafter refusing to stop quoting a « » span back at the partner. Author-scoped like
   * every other field here; a signal has never crossed to the other side at all.
   */
  draftNote?: string | null;
}

const KINDS: Kind[] = ['request', 'desire', 'frustration'];
const KIND_LABELS: Record<Kind, string> = { request: 'Request', desire: 'Desire', frustration: 'Frustration' };

/** The longest a signal may be, and where the counter starts showing itself. */
const BODY_MAX = 2000;
const COUNTER_FROM = 1600;

/**
 * What the app DOES with each kind — one line, shown for the chosen one only.
 *
 * The kind is not a label on the signal, it is what steers the drafter, and three bare chips left
 * the author guessing what picking one changed. Each line is the same shape on purpose: what it
 * becomes, then the thing it is deliberately not — because "never a complaint" is the promise the
 * box is making, and it is the reason to write the honest version rather than a diplomatic one.
 *
 * It used to be three tiles carrying all three lines at once, above the box. Three consequences
 * on screen is two the author is not choosing; the chosen one, under the row, is the whole of
 * what the tiles were for at a tenth of the height.
 *
 * The glyphs are the panels the kind usually lands in (Missions, Dates) and, for a frustration,
 * two halves of one circle: a we-problem is neither person's.
 */
const KIND_LINES: Record<Kind, { glyph: string; line: string }> = {
  request: { glyph: '◎', line: 'a request becomes a mission they choose to take, never a chore assigned' },
  desire: { glyph: '◈', line: 'a desire becomes a planted idea — a date, a question, a nudge — never a hint' },
  frustration: { glyph: '◐', line: 'a frustration becomes a we-problem the game proposes, never a complaint' },
};

/** How a step can be asked for again. `oblique` is what the endpoint does with no tone at all. */
type Tone = 'oblique' | 'softer' | 'shorter';
const TONES: { tone: Tone; label: string }[] = [
  { tone: 'oblique', label: 'More oblique' },
  { tone: 'softer', label: 'Softer' },
  { tone: 'shorter', label: 'Shorter' },
];

/**
 * A row of chips that never orphans one.
 *
 * `grow basis-[92px]` rather than intrinsic widths: every chip in the row is the same size and the
 * row breaks as a whole, so "Shorter" cannot end up alone on a second line the way it did at 390
 * and 320. Shared by the kind chooser and the redraft tones because it is the same object twice.
 */
const CHIP_CLASS = 'min-w-0 grow basis-[92px]';

const STATUS_COPY: Record<string, string> = {
  drafting: 'The AI is drafting a plan…',
  plan_ready: 'Plan ready — review it',
  approved: 'Approved. The app will pick its moments.',
  rejected: 'Rejected — nothing will surface.',
  done: 'Done. It happened, and it was never you.',
  draft_failed: 'Drafting failed — try again.',
  withdrawn: 'Withdrawn. Anything already out stays out.',
  expired: 'Expired quietly — it never found its moment.',
  new: 'Waiting…',
};

/**
 * How long something has been going, in the roughest unit that still says something.
 *
 * A wait with no clock on it is the same screen at one minute and at forty, which is how "a few
 * minutes" reads as a lie. Minutes under an hour, then hours — no seconds, because a number that
 * moves every tick makes a slow thing look broken.
 */
function elapsed(from: string | number, now: number): string {
  const started = typeof from === 'string' ? Date.parse(from) : from;
  if (!Number.isFinite(started)) return '';
  const minutes = Math.max(0, Math.floor((now - started) / 60_000));
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h`;
}

/**
 * What one step's line says once the plan is out of review — and only what the timeline cannot.
 *
 * "out in the world", "completed by them" and "expired quietly" have moved to the timeline below,
 * where they are allow-listed sentences chosen by the server rather than words this file guessed
 * from a status. What is left are the three states no event was ever recorded for: a step the
 * author struck, one they withdrew, and one that has not gone out yet. Silence for those would
 * read as a bug rather than as a wait.
 */
function stepLine(step: PlanStep): string {
  if (step.status === 'rejected') return 'struck by you';
  if (step.withdrawn) return 'withdrawn before it went out';
  if (step.surfaced || step.done || step.expired) return '';
  return 'waiting for its moment';
}

export function SignalsScreen() {
  const { me, selected, select, fail, openOverlay, signalDraft, setSignalDraft } = useStore();
  // Tuxedo keeps red for three objects only (primary button, dock badge, meter fill), so an
  // emphasis edge there is gold; Bouquet's accent is the red itself.
  const look = useStore((s) => s.look);
  const emphasisEdge = look === 'tuxedo' ? 'border-gold' : 'border-primary';
  const activeId = selected.signals ?? null;
  const them = me?.partnerName ?? 'them';

  /**
   * THE BOX SURVIVES A TAB SWITCH. `App.tsx` renders one screen at a time, so component state here
   * is state that dies the moment somebody checks Missions mid-sentence — in the one box in the app
   * whose whole purpose is that the hard version gets written. Store state, cleared on a
   * successful drop and never persisted to disk. See `store.ts`.
   */
  const compose = useStore((s) => s.compose);
  const setCompose = useStore((s) => s.setCompose);
  const clearCompose = useStore((s) => s.clearCompose);
  const kind = compose.kind;
  const draft = compose.body;

  const [signals, setSignals] = React.useState<SignalSummary[]>([]);
  const [active, setActive] = React.useState<SignalDetail | null>(null);
  const [edits, setEdits] = React.useState<Record<string, string>>({});
  const [struck, setStruck] = React.useState<Record<string, boolean>>({});
  /**
   * The phrasing that was on screen when a rewrite was asked for, per step.
   *
   * Held here rather than read back off the step, because the step's phrasing is the thing that
   * changes: once the rewrite lands there is nothing left on the row saying what it replaced. It
   * is component state and deliberately not persisted — "what this said a minute ago" is a fact
   * about this review sitting, and a Before line surviving a reload would be pinned above wording
   * nobody in the room ever saw.
   */
  const [before, setBefore] = React.useState<Record<string, string>>({});
  const [confirmWithdraw, setConfirmWithdraw] = React.useState(false);
  /**
   * REJECTION IS CONFIRMED, exactly as withdrawal is.
   *
   * "Reject the whole thing" threw away a plan the gateway had spent minutes on, in one
   * unconfirmed tap, sitting directly under an approve button of the same width — while withdraw,
   * the gentler of the two, had a whole confirm card. Same card shape for both.
   */
  const [confirmReject, setConfirmReject] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  /**
   * The number of steps the approval put in flight, while the stamp band is up.
   *
   * Approval was a status string quietly changing from "Plan ready" to "Approved" — no moment at
   * all for the one irreversible thing on this screen. It is the same band the boards use for a
   * completed mission, held the same length of time, on the card the press happened on; the status
   * line underneath is what it settles into.
   */
  const [approvedStamp, setApprovedStamp] = React.useState<number | null>(null);
  const stampTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only this device knows when Make it more oblique was pressed — the server records no such
  // moment — so the elapsed part of the rewrite caption is simply absent after a reload.
  const [rewriteAskedAt, setRewriteAskedAt] = React.useState<Record<string, number>>({});
  const [now, setNow] = React.useState(() => Date.now());

  // The waits here are minutes long, so the labels have to move between polls rather than only
  // when one lands.
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(
    () => () => {
      if (stampTimer.current) clearTimeout(stampTimer.current);
    },
    [],
  );

  const open = React.useCallback(async (id: string) => {
    const detail = await api.get<SignalDetail>(`/api/signals/${id}`).catch(() => null);
    if (detail) setActive(detail);
  }, []);

  const load = React.useCallback(async () => {
    setSignals(await api.get<SignalSummary[]>('/api/signals'));
    // While the gateway is drafting — or rewriting one step — the plan lands without anything
    // happening on this device.
    if (activeId) await open(activeId);
  }, [activeId, open]);

  const { loaded, error, reload } = usePoll(load, 12_000);

  /**
   * The walkthrough opens itself, once, on the first visit — and a signal composed elsewhere
   * (Sync's "make it a signal") arrives in the box the same way.
   */
  React.useEffect(() => {
    if (me && !me.walkthroughSeen) openOverlay('walkthrough');
  }, [me?.walkthroughSeen]);

  React.useEffect(() => {
    if (!signalDraft) return;
    setCompose({ kind: signalDraft.kind, body: signalDraft.body });
    setSignalDraft(null); // consumed: coming back later must not re-fill an emptied box
  }, [signalDraft, setSignalDraft, setCompose]);

  React.useEffect(() => {
    setEdits({});
    setStruck({});
    setBefore({});
    setConfirmWithdraw(false);
    setConfirmReject(false);
    if (!activeId) setActive(null);
    else void open(activeId);
  }, [activeId, open]);

  const create = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      const created = await api.post<{ id: string }>('/api/signals', { kind, body });
      clearCompose();
      await load();
      select('signals', created.id);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Approve, then STAMP — and only reload once the band has been read.
   *
   * The refresh is what takes the review apart (the status stops being `plan_ready`), so it waits
   * out the hold: `reviewing` below stays true while a stamp is up, and `busy` stays true with it
   * so nothing underneath can be pressed through the band.
   */
  const approve = async () => {
    if (!active?.plan) return;
    const id = active.id;
    const steps = active.plan.steps;
    setBusy(true);
    try {
      await api.post(`/api/signals/${id}/approve`, {
        steps: steps.map((step) => ({
          id: step.id,
          phrasing: edits[step.id],
          rejected: !!struck[step.id],
        })),
      });
      const inFlight = steps.filter((step) => !struck[step.id] && step.status === 'pending').length;
      setEdits({});
      setStruck({});
      // Approval is what the Before lines were leading up to: after it, the wording a rewrite
      // replaced is a claim about a draft that no longer exists.
      setBefore({});
      setApprovedStamp(inFlight);
      stampTimer.current = setTimeout(() => {
        setApprovedStamp(null);
        setBusy(false);
        void Promise.all([open(id), load()]);
      }, STAMP_HOLD_MS);
    } catch (e) {
      fail(e);
      setBusy(false);
    }
  };

  const simplePost = async (path: string, body?: unknown) => {
    if (!active) return;
    try {
      await api.post(path, body);
      await Promise.all([open(active.id), load()]);
    } catch (e) {
      fail(e);
    }
  };

  /**
   * Ask for one step to be written again, in a named tone.
   *
   * The wording on screen is remembered FIRST, before the request: the POST puts the step into
   * `redrafting` and the next poll replaces the phrasing, so a snapshot taken afterwards would
   * already be the new words — and the Before line would say the rewrite changed nothing.
   */
  const redraft = async (step: PlanStep, tone: Tone) => {
    if (!active) return;
    setBefore((b) => ({ ...b, [step.id]: edits[step.id] ?? step.phrasing }));
    setRewriteAskedAt((r) => ({ ...r, [step.id]: Date.now() }));
    await simplePost(`/api/signals/${active.id}/steps/${step.id}/oblique`, { tone });
  };

  const list = (
    <>
      <Card>
        <CardContent>
          <CardHead>Say the honest thing</CardHead>
          <CardDescription>
            Never shown to them, not a word of it — it becomes anonymous missions, planted ideas and quiet questions,
            and you approve every one first.
          </CardDescription>

          <Textarea
            value={draft}
            onChangeText={(body) => setCompose({ body })}
            maxLength={BODY_MAX}
            placeholder="A request, a desire, a frustration — in your own words."
          />
          {/* A counter that only appears when the cap is close enough to matter. Shown from the
              first character it would be a word count somebody is being marked on. */}
          {draft.length > COUNTER_FROM ? (
            <Text variant="note" className={cn('self-end', draft.length >= BODY_MAX && 'text-destructive')}>
              {draft.length} / {BODY_MAX}
            </Text>
          ) : null}

          {/* THE KIND, UNDER THE BOX. One row, equal widths, and the consequence of the one that
              is chosen underneath it — see `KIND_LINES`. */}
          <View className="flex-row flex-wrap gap-2">
            {KINDS.map((k) => (
              <Chip
                key={k}
                className={CHIP_CLASS}
                label={`${KIND_LINES[k].glyph}  ${KIND_LABELS[k]}`}
                selected={kind === k}
                onPress={() => setCompose({ kind: k })}
              />
            ))}
          </View>
          <Text variant="small" className="text-muted-foreground">
            {KIND_LINES[kind].line}
          </Text>

          <View className="flex-row flex-wrap items-center justify-between gap-2">
            <Button disabled={!draft.trim() || busy} reason="Write something first" onPress={create}>
              <Text>Drop it in the box</Text>
            </Button>
            <Button variant="ghost" size="sm" flush={false} onPress={() => openOverlay('prefs')}>
              <Text>Preference sheet</Text>
            </Button>
          </View>
          <Text variant="small" className="text-muted-foreground">
            Never quoted. Names and places are swapped out before it leaves your device.
          </Text>
        </CardContent>
      </Card>

      {signals.length ? (
        signals.map((s) => (
          <Pressable key={s.id} onPress={() => select('signals', s.id)}>
            <Card className={activeId === s.id ? emphasisEdge : undefined}>
              <CardContent className="gap-1">
                <View className="flex-row flex-wrap items-center gap-2">
                  {/* The kind is a FACT, so it takes the base badge. The filled one is reserved
                      for the thing that wants a press — see the Review pill beside it. */}
                  <Badge variant="secondary">
                    <Text>{KIND_LABELS[s.kind]}</Text>
                  </Badge>
                  <View className="min-w-0 flex-1" />
                  {s.status === 'plan_ready' ? (
                    <Badge variant="default">
                      <Text>Review</Text>
                    </Badge>
                  ) : null}
                </View>
                {/* Three lines, not two. At 320 a two-line clamp hid the actual ask on half the
                    cards — "I want an evenin…" is a card that says nothing at all. */}
                <Text numberOfLines={3}>{s.body}</Text>
                {/* THE STATUS IS THE CARD'S SECOND LINE, not its smallest print. It is the only
                    thing on the card that says what to do, and it was the lowest-weight text on a
                    tappable row with nothing marking the row as tappable. */}
                <View className="flex-row items-center gap-2">
                  <Text className="text-foreground min-w-0 flex-1 font-medium">
                    {STATUS_COPY[s.status] ?? s.status}
                  </Text>
                  <Text className="text-muted-foreground text-[19px] leading-[19px]">›</Text>
                </View>
              </CardContent>
            </Card>
          </Pressable>
        ))
      ) : (
        <Empty mark="▣" title="Nothing yet" body="The box is safe — that is the whole point. Say the thing you would otherwise swallow." />
      )}
    </>
  );

  // The stamp holds the review together for its own 700ms: the approval has landed server-side, so
  // without this the card carrying the band would be gone before the band could be read.
  const reviewing = !!active?.plan && (active.status === 'plan_ready' || approvedStamp !== null);
  const rewriting = !!active?.plan?.steps.some((s) => s.redrafting);
  const unsurfaced = active?.plan?.steps.filter((s) => !s.surfaced && s.status !== 'rejected' && !s.withdrawn && !s.expired) ?? [];

  const detail =
    activeId && active ? (
      <>
        <Button variant="ghost" size="sm" className="self-start" onPress={() => select('signals', null)}>
          <Text>← All signals</Text>
        </Button>

        <Card>
          <CardContent className="gap-1">
            <Badge variant="secondary">
              <Text>{KIND_LABELS[active.kind]}</Text>
            </Badge>
            <Text>“{active.body}”</Text>
            <Text variant="small" className="text-muted-foreground">
              {STATUS_COPY[active.status] ?? active.status}
            </Text>
            {/* "Try again" is useless advice if trying again does the same thing. When the server
                knows what actually went wrong, it says so here, under the status it explains. */}
            {active.draftNote ? (
              <Text variant="small" className="text-destructive">
                {active.draftNote}
              </Text>
            ) : null}
          </CardContent>
        </Card>

        {reviewing ? (
          <>
            {/* The band lands on THIS card — the plan's own — which is where the approve button
                sits at the foot of the same column. `View` rather than a fragment because
                `StampBand` is absolutely positioned and needs a box to be absolute within. */}
            <View>
              <Card>
                <CardContent>
                  <CardHead>The plan</CardHead>
                  {active.plan!.summary ? <CardDescription>{active.plan!.summary}</CardDescription> : null}
                  <Text variant="small" className="text-muted-foreground">
                    Edit any wording. Strike anything. What you approve is EXACTLY what they will see — with no name on
                    it, at moments the app chooses. No timeframes, ever.
                  </Text>
                </CardContent>
              </Card>
              {approvedStamp !== null ? (
                <StampBand
                  kind="done"
                  label={`Approved · ${approvedStamp} step${approvedStamp === 1 ? '' : 's'} in flight`}
                />
              ) : null}
            </View>

            {active.plan!.steps.map((step) => {
              // Probes are the exception to "the plan approval is the only approval": they ask the
              // partner something and reveal nothing about the author, so they run at draft time
              // and are already out by the time this screen is read. Editing, rewriting or
              // striking one would all be lies about what is still possible.
              const editable = step.status === 'pending' || step.redrafting;
              // The API defaults an unrated step to medium on purpose, and says so in the note.
              const unrated = step.traceNote === 'not rated';
              const askedAt = rewriteAskedAt[step.id];
              const value = edits[step.id] ?? step.phrasing;
              const previous = before[step.id];
              // A rewrite has LANDED when the words differ from the ones that were on screen when
              // it was asked for. `null` rather than a boolean so the Before line narrows to a
              // string without a cast.
              const landed = previous !== undefined && previous !== value ? previous : null;
              return (
                <Card key={step.id} className={struck[step.id] ? 'opacity-50' : undefined}>
                  <CardContent>
                    <View className="flex-row flex-wrap items-center gap-2">
                      {/* ONE CHIP STYLE IN THIS CARD, and it is the filled one: what this step
                          will BECOME on the other phone. The trace is a line of prose below,
                          because a coloured pill is a claim about importance and the trace is a
                          reading — a tan chip beside a pink one was two labels arguing about
                          which of them the eye should take first. */}
                      <Badge variant="default">
                        <Text>{step.label}</Text>
                      </Badge>
                      {!editable ? (
                        <Badge variant="secondary">
                          <Text>already asked</Text>
                        </Badge>
                      ) : null}
                    </View>
                    <Text variant="note">
                      {unrated ? 'Trace: not rated · treated as medium' : `Trace: ${step.traceability}`}
                      {step.traceNote && !unrated ? ` · ${step.traceNote}` : ''}
                    </Text>

                    {step.redrafting ? (
                      <>
                        {/* The Before line stays up while the gateway thinks: what is being
                            replaced is the one thing worth reading in the seconds it takes. On a
                            reload there is no snapshot, so the current wording stands in for it. */}
                        {previous !== undefined ? (
                          <BeforeLine text={previous} />
                        ) : (
                          <Text className="text-muted-foreground">{step.phrasing}</Text>
                        )}
                        <Text variant="small" className="text-muted-foreground">
                          {`Rewriting…${askedAt ? ` ${elapsed(askedAt, now)}` : ''} — the current wording stays until a better one lands`}
                        </Text>
                        {/* The way out of a wait that can take minutes. It sits here, next to the
                            wait it abandons, rather than down with the redraft chips. */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="self-start"
                          disabled={busy}
                          onPress={() => simplePost(`/api/signals/${active.id}/steps/${step.id}/keep`)}>
                          <Text>Keep the current wording</Text>
                        </Button>
                      </>
                    ) : editable ? (
                      <>
                        {landed ? (
                          <View className="border-border gap-2 rounded-md border p-3">
                            <BeforeLine text={landed} />
                            <MarkedPhrasing before={landed} after={value} />
                            <Text variant="small" className="text-muted-foreground">
                              Marked words are what the rewrite changed — watch the trace line above move with them.
                              Editing or approving clears this.
                            </Text>
                          </View>
                        ) : null}
                        <Textarea
                          value={value}
                          editable={!struck[step.id]}
                          onChangeText={(text) => {
                            setEdits((e) => ({ ...e, [step.id]: text }));
                            // The author has taken the wording over, so the Before line is no
                            // longer about anything on screen.
                            setBefore(({ [step.id]: _dropped, ...rest }) => rest);
                          }}
                        />
                        <Text variant="label">Redraft</Text>
                        <View className="flex-row flex-wrap gap-2">
                          {TONES.map(({ tone, label }) => (
                            <Chip
                              key={tone}
                              className={CHIP_CLASS}
                              label={label}
                              disabled={!!struck[step.id] || busy}
                              onPress={() => void redraft(step, tone)}
                            />
                          ))}
                        </View>
                        <StepPreview step={{ panel: step.panel, phrasing: value, payload: step.payload }} partnerName={them} />
                      </>
                    ) : (
                      <>
                        <Text>{step.phrasing}</Text>
                        <Text variant="small" className="text-muted-foreground">
                          A scouting question needs no approval — it reveals nothing about you, so it went out with
                          the draft, mixed in with unrelated ones.
                        </Text>
                      </>
                    )}

                    {editable ? (
                      <View className="flex-row flex-wrap gap-2">
                        {/* A GHOST, not a fill. Struck four times down one card, "Strike this
                            step" was the most saturated red on the screen — louder than the
                            approve button, for the action that undoes one line. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={step.redrafting}
                          onPress={() => setStruck((s) => ({ ...s, [step.id]: !s[step.id] }))}>
                          <Text className={struck[step.id] ? undefined : 'text-destructive'}>
                            {struck[step.id] ? 'Restore step' : 'Strike this step'}
                          </Text>
                        </Button>
                      </View>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}

            <Text variant="small" className="text-muted-foreground">
              Steps that never find their moment expire quietly after three weeks. You can withdraw the whole signal
              any time before a step is out.
            </Text>

            {/* THE BUTTON'S LABEL DOES NOT MOVE. It grew into a three-line sentence whenever a
                rewrite was pending — the one control the whole screen leads to, changing shape
                and height under the finger. What it needs to say lives above it instead. */}
            <Text variant="note">
              {rewriting
                ? 'Waiting for a rewrite — or keep the current wording above.'
                : 'This is the only approval. After it, the app picks the moments.'}
            </Text>
            <Button disabled={busy || rewriting} onPress={approve}>
              <Text>Approve the plan</Text>
            </Button>

            {confirmReject ? (
              <Card className="border-destructive">
                <CardContent>
                  <CardHead>Reject the whole plan?</CardHead>
                  <CardDescription>
                    Every step goes, including any wording you have edited. Nothing will surface and {them} is told
                    nothing — you can ask for a fresh plan on the same words afterwards.
                  </CardDescription>
                  <View className="flex-row flex-wrap gap-2">
                    <Button
                      variant="destructive"
                      onPress={async () => {
                        setConfirmReject(false);
                        await simplePost(`/api/signals/${active.id}/reject`);
                      }}>
                      <Text>Reject the plan</Text>
                    </Button>
                    <Button variant="ghost" flush={false} onPress={() => setConfirmReject(false)}>
                      <Text>Keep reviewing</Text>
                    </Button>
                  </View>
                </CardContent>
              </Card>
            ) : (
              <Button variant="outline" disabled={busy} onPress={() => setConfirmReject(true)}>
                <Text className="text-destructive">Reject the whole thing</Text>
              </Button>
            )}
          </>
        ) : active.plan ? (
          <>
            {active.plan.steps.map((step) => (
              <Card key={step.id}>
                <CardContent className="gap-1">
                  <Badge variant="default">
                    <Text>{step.label}</Text>
                  </Badge>
                  <Text>{step.phrasing}</Text>
                  {stepLine(step) ? (
                    <Text variant="small" className="text-muted-foreground">
                      {stepLine(step)}
                    </Text>
                  ) : null}
                </CardContent>
              </Card>
            ))}

            {/* WHAT ACTUALLY HAPPENED, under the plan rather than on it.
                Per-step status words could only ever say what the step's own row knew, which is
                why "out in the world" sat there for weeks after a mission was quietly skipped.
                These lines come from the server's allow-list, one per real event, in the order
                they happened — and the ones that are missing are missing on purpose. */}
            {active.timeline?.length ? (
              <Card>
                <CardContent className="gap-2">
                  <CardHead>What came of it</CardHead>
                  {active.timeline.map((entry, i) => (
                    <View key={`${entry.week}-${i}`} className="flex-row flex-wrap items-baseline gap-2">
                      <Text variant="small" className="text-muted-foreground w-[74px]">
                        {entry.week}
                      </Text>
                      <Text className="flex-1 min-w-[180px]">{entry.line}</Text>
                    </View>
                  ))}
                </CardContent>
              </Card>
            ) : null}
          </>
        ) : active.status === 'drafting' ? (
          <Empty
            mark="⋯"
            title="Drafting"
            body="The plan is being written on the AI gateway. It usually lands within a few minutes, sometimes longer, and it keeps going with the app closed — check back."
            action={
              <View className="items-center gap-1">
                <Text variant="small" className="text-muted-foreground">
                  Started {elapsed(active.createdAt, now)} ago
                </Text>
                {now - Date.parse(active.createdAt) >= 10 * 60_000 ? (
                  <Text variant="small" className="text-muted-foreground max-w-[380px] text-center">
                    Taking longer than usual. It is still queued — you can withdraw it any time.
                  </Text>
                ) : null}
              </View>
            }
          />
        ) : null}

        {['drafting', 'plan_ready', 'draft_failed', 'approved'].includes(active.status) ? (
          confirmWithdraw ? (
            <Card className="border-destructive">
              <CardContent>
                <CardHead>{unsurfaced.length ? 'Withdraw the rest?' : 'Withdraw this?'}</CardHead>
                <CardDescription>
                  {unsurfaced.length
                    ? `${unsurfaced.length} step${unsurfaced.length === 1 ? '' : 's'} that ${unsurfaced.length === 1 ? 'has' : 'have'} not gone out will be pulled. Anything already out stays out — ${them} is told nothing either way.`
                    : `Nothing has gone out yet, so nothing will. ${them} is told nothing.`}
                </CardDescription>
                <View className="flex-row flex-wrap gap-2">
                  <Button
                    variant="destructive"
                    onPress={async () => {
                      setConfirmWithdraw(false);
                      await simplePost(`/api/signals/${active.id}/withdraw`);
                    }}>
                    <Text>Withdraw</Text>
                  </Button>
                  <Button variant="ghost" flush={false} onPress={() => setConfirmWithdraw(false)}>
                    <Text>Leave it running</Text>
                  </Button>
                </View>
              </CardContent>
            </Card>
          ) : (
            <Button variant="outline" size="sm" className="self-start" onPress={() => setConfirmWithdraw(true)}>
              <Text className="text-destructive">{active.status === 'approved' ? 'Withdraw the rest' : 'Withdraw'}</Text>
            </Button>
          )
        ) : null}

        {active.status === 'rejected' || active.status === 'draft_failed' ? (
          <Button onPress={() => simplePost(`/api/signals/${active.id}/redraft`)}>
            <Text>Ask for a fresh plan</Text>
          </Button>
        ) : null}
      </>
    ) : undefined;

  if (!loaded) return (
    <Panel id="signals">
      <PanelSkeleton rows={3} />
    </Panel>
  );
  if (error) return (
    <Panel id="signals">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  return (
    <Panel
      id="signals"
      detail={detail}
      guideAction={
        <Button variant="secondary" size="sm" onPress={() => openOverlay('walkthrough')}>
          <Text>Show me how this works again</Text>
        </Button>
      }>
      {list}
    </Panel>
  );
}

/**
 * What a step said before the rewrite, pinned above what it says now.
 *
 * A pill rather than the word "before" in the sentence, because this line is evidence and not
 * copy: it has to be readable as "not the current wording" at a glance, next to a line that reads
 * almost the same. Muted for the same reason — the phrasing under approval is the one that counts.
 */
function BeforeLine({ text }: { text: string }) {
  return (
    <View className="flex-row flex-wrap items-center gap-2">
      <Badge variant="outline">
        <Text>Before</Text>
      </Badge>
      <Text variant="small" className="text-muted-foreground min-w-0 grow basis-[160px]">
        {text}
      </Text>
    </View>
  );
}
